import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog, PageLoader, EmptyState, Badge, ActionBtn, SectionHeader, FormField, Pagination } from '../components/common/UI';
import { EMPLOYEE_STATUS, EMPLOYEE_ROLES } from '../utils/statusHelpers';
import { toShamsi, formatCurrency } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import { MdAdd, MdEdit, MdDelete, MdAccessTime } from '../utils/icons';
import ShamsiDatePicker from '../components/common/ShamsiDatePicker';
const Clock = ({ size = 14 }) => <MdAccessTime size={size} />;
import EmployeeModal from '../components/common/EmployeeModal';

const initialForm = { name: '', role: 'Editor', phone: '', email: '', salary: '', salaryType: 'Monthly', status: 'Active', notes: '' };

export default function Employees() {
  const { isManager } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTimeModal, setShowTimeModal] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [timeForm, setTimeForm] = useState({ hours: '', description: '', date: '' });
  const [saving, setSaving] = useState(false);
  const [filterRole, setFilterRole] = useState('');
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);


  
  const load = useCallback(async () => {
    try { const { data } = await API.get('/employees'); setEmployees(data); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = employees.filter(e => !filterRole || e.role === filterRole);

  // پیج‌بندی (صفحه‌بندی سمت کلاینت)
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [filterRole]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openAdd = () => { setEditEmployee(null); setShowEmployeeModal(true); };
  const openEdit = (e) => { setEditEmployee(e); setShowEmployeeModal(true); };

  const handleSave = async () => {
    if (!form.name || !form.phone) return toast.error('نام و شماره تلفن ضروری است');
    setSaving(true);
    try {
      if (editItem) { await API.put(`/employees/${editItem._id}`, form); toast.success('کارمند بروزرسانی شد'); }
      else { await API.post('/employees', form); toast.success('کارمند اضافه شد'); }
      setShowModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'خطا'); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await API.delete(`/employees/${deleteId}`); toast.success('کارمند غیرفعال شد'); setDeleteId(null); load(); }
    catch { toast.error('خطا'); }
  };

  const saveTimeLog = async () => {
    if (!timeForm.hours) return toast.error('ساعت را وارد کنید');
    try {
      await API.post(`/employees/${showTimeModal}/timelog`, { ...timeForm, hours: +timeForm.hours });
      toast.success('ساعت کار ثبت شد');
      setShowTimeModal(null);
      setTimeForm({ hours: '', description: '', date: '' });
      load();
    } catch { toast.error('خطا'); }
  };

  const totalHours = (emp) => emp.timeLogs?.reduce((sum, l) => sum + l.hours, 0) || 0;

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader title="کارمندان" subtitle={`${filtered.length} کارمند`}
        actions={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="کارمند جدید" />} />

      {/* Filter */}
      <div className="glass-card p-4">
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="form-input w-full sm:w-48">
          <option value="">همه نقش‌ ها</option>
          {Object.entries(EMPLOYEE_ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? <EmptyState icon="👥" title="کارمندی یافت نشد" action={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="کارمند جدید" />} /> : (
        <>
          {/* Desktop Table */}
          <div className="glass-card overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="premium-table">
                <thead>
                  <tr><th>نام</th><th>نقش</th><th>تلفن</th><th>معاش</th><th>نوع معاش</th><th>ساعات کار</th><th>وضعیت</th><th>عملیات</th></tr>
                </thead>
                <tbody>
                  {paged.map(e => (
                    <tr key={e._id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{e.name.charAt(0)}</div>
                          <div>
                            <div className="font-semibold text-gray-800">{e.name}</div>
                            {e.email && <div className="text-xs text-gray-600">{e.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="text-gray-600">{EMPLOYEE_ROLES[e.role] || e.role}</td>
                      <td className="text-gray-600 font-mono text-sm">{e.phone}</td>
                      <td className="text-green-600">{formatCurrency(e.salary)}</td>
                      <td className="text-gray-500 text-sm">{{ Monthly: 'ماهانه', PerProject: 'هر پروژه', Hourly: 'ساعتی' }[e.salaryType]}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-800">{totalHours(e)} ساعت</span>
                          <button onClick={() => setShowTimeModal(e._id)} className="p-1 text-blue-600 hover:text-blue-700 transition-colors" title="ثبت ساعت"><Clock size={14} /></button>
                        </div>
                      </td>
                      <td><Badge statusConfig={EMPLOYEE_STATUS[e.status]} /></td>
                      <td>
                        <div className="flex items-center gap-1">
                          {isManager && <button onClick={() => openEdit(e)} className="p-1.5 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100 rounded-lg transition-all"><MdEdit size={15} /></button>}
                          {isManager && <button onClick={() => setDeleteId(e._id)} className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-all"><MdDelete size={15} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {paged.map(e => (
              <div key={e._id} className="mobile-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">{e.name.charAt(0)}</div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">{e.name}</div>
                    <div className="text-sm text-gray-500">{EMPLOYEE_ROLES[e.role]} • {e.phone}</div>
                  </div>
                  <Badge statusConfig={EMPLOYEE_STATUS[e.status]} />
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-600 text-xs">معاش:</span><br /><span className="text-green-600">{formatCurrency(e.salary)}</span></div>
                  <div><span className="text-gray-600 text-xs">ساعات کار:</span><br /><span className="text-gray-800">{totalHours(e)} ساعت</span></div>
                </div>
                {isManager && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => setShowTimeModal(e._id)} className="flex-1 py-1.5 text-xs bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors">⏱️ ثبت ساعت</button>
                    <button onClick={() => openEdit(e)} className="flex-1 py-1.5 text-xs bg-gray-50 text-gray-800 rounded-lg hover:bg-gray-100 transition-colors">ویرایش</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}

<EmployeeModal 
  isOpen={showEmployeeModal} 
  onClose={() => setShowEmployeeModal(false)} 
  onSuccess={load}
  editItem={editEmployee}
/>

      {/* Time Log Modal */}
      <Modal isOpen={!!showTimeModal} onClose={() => setShowTimeModal(null)} title="ثبت ساعت کار" size="sm">
        <div className="space-y-4">
          <FormField label="تاریخ"><ShamsiDatePicker value={timeForm.date} onChange={date => setTimeForm({ ...timeForm, date: date })} className="form-input" /></FormField>
          <FormField label="ساعات کار" required><input type="number" className="form-input" value={timeForm.hours} onChange={e => setTimeForm({ ...timeForm, hours: e.target.value })} placeholder="8" min="0.5" step="0.5" /></FormField>
          <FormField label="توضیح"><input className="form-input" value={timeForm.description} onChange={e => setTimeForm({ ...timeForm, description: e.target.value })} placeholder="چه کاری انجام شد..." /></FormField>
        </div>
        <div className="flex gap-3 justify-end mt-5">
          <button onClick={() => setShowTimeModal(null)} className="px-4 py-2 rounded-xl bg-gray-100 text-gray-800 text-sm hover:bg-gray-200 transition-colors">انصراف</button>
          <ActionBtn onClick={saveTimeLog} label="ثبت" />
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="غیرفعال کردن کارمند" message="آیا مطمئن هستید؟" />
    </div>
  );
}
